/** WARNING! This does look slightly strange!
 *  It uses `module.exports` instead of exporting individual functions.
 *  This is because this is used by both Explorer on the web
 *  and by the CLI utility that generates a schema object.
 */

const GeojsonSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    coordinates: {
      anyOf: [
        {
          type: 'array',
          items: {
            type: 'number',
          },
        },
        {
          type: 'array',
          items: {
            type: 'array',
            items: {
              type: 'number',
            },
          },
        },
        {
          type: 'array',
          items: {
            type: 'array',
            items: {
              type: 'array',
              items: {
                type: 'number',
              },
            },
          },
        },
        {
          type: 'array',
          items: {
            type: 'array',
            items: {
              type: 'array',
              items: {
                type: 'array',
                items: {
                  type: 'number',
                },
              },
            },
          },
        },
      ],
    },
    type: {
      type: 'string',
    },
  },
};

const SearchFilter = {
  anyOf: [
    {
      type: 'null',
    },
    {
      type: 'array',
      items: {
        type: 'number',
      },
      maxItems: 2,
    },
  ],
};

/**
 * The keys of this object correspond to the keys in the redux store.
 *
 */
export const schema = {
  version: {
    type: 'number',
  },
  areaCoverage: {
    type: 'array',
    items: {
      type: 'number',
    },
    maxItems: 2,
  },
  'folders.selectedFolderId': {
    type: 'string',
    maxLength: 300,
  },
  'folders.viewItems': {
    type: 'array',
    items: {
      type: 'string',
      maxLength: 300,
    },
    maxItems: 1000,
  },
  'ui.mode': {
    type: 'string',
    maxLength: 64,
  },
  'ui.drawers.left': {
    type: 'string',
    maxLength: 64,
  },
  'ui.drawers.right': {
    type: 'string',
    maxLength: 64,
  },
  'map.center': {
    type: 'array',
    items: {
      type: 'number',
    },
    minItems: 2,
    maxItems: 2,
  },
  'map.zoom': {
    type: 'number',
  },
  'tiles.process': {
    anyOf: [
      {
        type: 'string',
        maxLength: 128,
      },
      {
        type: 'null',
      },
    ],
  },
  'tiles.processColor': {
    anyOf: [
      {
        type: 'string',
        maxLength: 128,
      },
      {
        type: 'null',
      },
    ],
  },
  'tiles.enhance.mode': {
    type: 'string',
    maxLength: 32,
  },
  'tiles.enhance.filters.filterSaturation': {
    type: 'number',
  },
  'tiles.enhance.filters.filterBrightness': {
    type: 'number',
  },
  'tiles.enhance.filters.filterContrast': {
    type: 'number',
  },
  'tiles.mode': {
    type: 'string',
    maxLength: 128,
  },
  'map.overlays': {
    type: 'array',
    items: {
      type: 'object',
      additionalProperties: false,
      properties: {
        name: {
          type: 'string',
          maxLength: 32,
        },
        on: {
          type: 'boolean',
        },
      },
    },
    additionalProperties: false,
    maxItems: 10,
  },
  'mosaics.interval': {
    type: 'string',
    maxLength: 256,
  },
  // This is the legacy selected series ID.
  //  it is preserved in the schema for legacy validation.
  'mosaics.selectedSeriesId': {
    type: 'string',
    maxLength: 256,
  },
  'mosaics.selectedName': {
    type: 'string',
    maxLength: 256,
  },
  'search.dateRanges': {
    type: 'array',
    items: {
      type: 'array',
      items: {
        type: 'string',
      },
      maxItems: 2,
    },
    maxItems: 12, // this is an artificial constraint
  },
  'search.geometry': {
    anyOf: [
      {
        type: 'null',
      },
      {
        ...GeojsonSchema,
      },
    ],
  },
  'search.page': {
    type: 'number',
  },
  'search.showCenterMarker': {
    anyOf: [
      {
        type: 'null',
      },
      {
        type: 'boolean',
      },
    ],
  },
  'search.isGeometryTooComplex': {
    type: 'boolean',
  },
  'filters.sun_azimuth': SearchFilter,
  'filters.view_angle': SearchFilter,
  'filters.cloud_cover': SearchFilter,
  'filters.gsd': SearchFilter,
  'filters.sun_elevation': SearchFilter,
  'filters.ground_control': {
    anyOf: [
      {
        type: 'null',
      },
      {
        type: 'string',
        maxLength: 128,
      },
    ],
  },
  'filters.quality_category': {
    anyOf: [
      {
        type: 'null',
      },
      {
        type: 'string',
        maxLength: 128,
      },
    ],
  },
  itemTypes: {
    type: 'array',
    items: {
      type: 'object',
      additionalProperties: false,
      properties: {
        name: {
          type: 'string',
          maxLength: 255,
        },
        on: {
          type: 'boolean',
        },
      },
    },
    maxItems: 24,
  },
  'results.viewItems': {
    type: 'array',
    maxItems: 1000,
  },
  'results.orderItems': {
    type: 'array',
    maxItems: 1000,
  },
  'compare.method': {
    type: 'string',
    maxLength: 32,
  },
  'compare.subjects': {
    type: 'object',
    additionalProperties: false,
    properties: {
      0: {
        anyOf: [
          {
            type: 'null',
          },
          {
            type: 'string',
            maxLength: 512,
          },
          {
            type: 'object',
            additionalProperties: false,
            properties: {
              id: {
                type: 'string',
                maxLength: 512,
              },
              parent: {
                type: 'string',
                maxLength: 512,
              },
              type: {
                type: 'string',
                maxLength: 512,
              },
            },
          },
        ],
      },
      1: {
        anyOf: [
          {
            type: 'null',
          },
          {
            type: 'string',
            maxLength: 512,
          },
          {
            type: 'object',
            additionalProperties: false,
            properties: {
              id: {
                type: 'string',
                maxLength: 512,
              },
              parent: {
                type: 'string',
                maxLength: 512,
              },
              type: {
                type: 'string',
                maxLength: 512,
              },
            },
          },
        ],
      },
    },
  },
  'searchFilters.accessPermissions': {
    type: 'boolean',
  },
  'searchFilters.instruments': {
    type: 'array',
    items: {
      type: 'object',
      additionalProperties: false,
      properties: {
        name: {
          type: 'string',
          maxLength: 255,
        },
        on: {
          type: 'boolean',
        },
      },
    },
    maxItems: 10,
  },
  'searchFilters.selectedImageryCapabilities': {
    type: 'array',
    items: {
      type: 'string',
      maxLength: 512,
    },
    maxItems: 10,
  },
  'searchFilters.sr': {
    type: 'boolean',
  },
  'searchFilters.publishingStages': {
    type: 'array',
    items: {
      type: 'object',
      additionalProperties: false,
      properties: {
        name: {
          type: 'string',
          maxLength: 255,
        },
        on: {
          type: 'boolean',
        },
      },
    },
    maxItems: 10,
  },
  'searchFilters.onlyTasked': {
    type: 'boolean',
  },
  'series.selectedId': {
    type: 'string',
    maxLength: 256,
  },
};
